import { MenuDrawer } from '@/scripts/theme/menu-drawer'
import {
	currentTargetRequired,
	getAttributeOrThrow,
	qsaOptional,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { trapFocus } from '@/scripts/core/global'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global'
import { getSectionHTMLForResource } from '@/scripts/core/cart-functions'

export class HeaderDrawer extends MenuDrawer {
	static override htmlSelector = 'header-drawer'
	static override selectors = {
		...MenuDrawer.selectors,
	}
	header?: HTMLElement
	borderOffset?: number
	hoverLoadStatus: 'loaded' | 'not_loaded' = 'not_loaded'
	hoverTriggers?: NodeListOf<HTMLElement>
	hoverMediaContainers?: NodeListOf<HTMLElement>
	hoverMediaReplace?: HTMLElement

	constructor() {
		super()
		this.hoverTriggers = qsaOptional('[data-hover-trigger]', this)
		this.hoverMediaReplace = qsOptional('[data-hover-media-replace]', this)
	}

	override getInstanceSelectors() {
		this.instanceSelectors = HeaderDrawer.selectors
	}

	setHeader() {
		return this.header || qsRequired(SELECTORS.sectionHeader)
	}

	setBorderOffset() {
		return this.borderOffset ||
			qsRequired(SELECTORS.headerWrapper).dataset.ucHeaderWrapper ==
				'border-bottom'
			? 1
			: 0
	}

	override openMenuDrawer(summaryElement: HTMLElement) {
		this.header = this.setHeader()
		this.borderOffset = this.setBorderOffset()
		document.documentElement.style.setProperty(
			'--header-bottom-position',
			`${Math.round(
				this.header.getBoundingClientRect().bottom - this.borderOffset
			)}px`
		)
		this.header.setAttribute('data-uc-header-menu-open', '')

		setTimeout(() => {
			this.mainDetails.setAttribute(ATTRIBUTES.menuOpening, '')
		})

		summaryElement.setAttribute('aria-expanded', 'true')
		window.addEventListener('resize', this.onResize)
		trapFocus(this.mainDetails, summaryElement)
		document.body.classList.add(
			`overflow-hidden-${this.dataset.breakpoint}`
		)
		void this.initializeHoverMedia()
	}

	override closeMenuDrawer(
		event: Event | undefined,
		elementToFocus: HTMLElement | undefined = undefined
	) {
		if (!elementToFocus) return
		this.header = this.setHeader()
		super.closeMenuDrawer(event, elementToFocus)
		this.header.removeAttribute('data-uc-header-menu-open')
		window.removeEventListener('resize', this.onResize)
		document.body.classList.remove(
			`overflow-hidden-${this.dataset.breakpoint}`
		)
	}

	onResize = () => {
		if (!this.header) return
		this.borderOffset = this.setBorderOffset()
		document.documentElement.style.setProperty(
			'--header-bottom-position',
			`${Math.round(
				this.header.getBoundingClientRect().bottom - this.borderOffset
			)}px`
		)
		document.documentElement.style.setProperty(
			'--viewport-height',
			`${window.innerHeight}px`
		)
	}

	async fetchResourceResponse(path: string): Promise<string> {
		if (path === 'primary') return 'primary'
		let resourcePath = path

		try {
			const html = await getSectionHTMLForResource(
				resourcePath,
				'dynamic-hover-media'
			)
			if (!html) return 'not_found'
			return html
		} catch (e) {
			console.error(e)
			return 'not_found'
		}
	}

	async fetchAllResourceResponses(
		resourcePaths: { path: string, route: string}[]
	): Promise<Record<string, string>> {
		const promises = resourcePaths.map(({route}) =>
			this.fetchResourceResponse(route)
		)
		const resourceHTMLArr = await Promise.all(promises)
		const resourceHTMLMap: Record<string, string> = {}
		resourcePaths.forEach(({path}, index) => {
			resourceHTMLMap[path] = resourceHTMLArr[index]
		})
		return resourceHTMLMap
	}

	async initializeHoverMedia() {
		if (this.hoverLoadStatus === 'loaded') return
		if (!this.hoverTriggers || !this.hoverMediaReplace) return
		const triggerEls = Array.from(this.hoverTriggers)

		const paths = triggerEls.map(
			(trigger) =>
				{
					return {
						path: getAttributeOrThrow('data-hover-trigger', trigger),
						route: getAttributeOrThrow('data-resource-route', trigger),
					}
				}
		)
		const resourceHTMLMap = await this.fetchAllResourceResponses(paths)
		let htmlString = ''
		triggerEls.forEach((trigger) => {
			const path = getAttributeOrThrow('data-hover-trigger', trigger)
			const relatedMediaTrigger = triggerEls.find(
				(trigger) =>
					getAttributeOrThrow('data-hover-trigger', trigger) === path
			)
			if (!relatedMediaTrigger) return
			const html = resourceHTMLMap[path]
			if (
				html === 'primary' ||
				html.trim() === '' ||
				!html ||
				html === 'not_found'
			) {
				trigger.setAttribute('data-hover-trigger', 'primary')
			} else {
				htmlString = htmlString + html
			}
			trigger.addEventListener('mouseover', (event: Event) => {
				event.preventDefault()
				const currentTarget = currentTargetRequired(event)
				let path = getAttributeOrThrow(
					'data-hover-trigger',
					currentTarget
				)

				this.showMediaForPath(path)
			})
		})
		this.hoverMediaReplace.outerHTML = htmlString
		this.hoverMediaContainers = qsaOptional('[data-hover-media]', this)
		void window.Ucoast.mediaManager.preloadContainer(this)
		this.hoverMediaContainers?.forEach((container) => {
			if (container.classList.contains('active')) {
				void window.Ucoast.mediaManager.loadAllInContainer(container)
			}
		})
		triggerEls.forEach((trigger) => {
			if (this.triggerIsActive(trigger)) {
				const path = getAttributeOrThrow('data-hover-trigger', trigger)
				this.showMediaForPath(path)
			}
		})
		this.hoverLoadStatus = 'loaded'
	}
	triggerIsActive(trigger: HTMLElement) {
		const triggerLink = qsOptional('a', trigger)
		return triggerLink?.hasAttribute('aria-current')
	}
	showMediaForPath(path: string) {
		this.hoverMediaContainers?.forEach((container) => {
			const mediaContainerPath = getAttributeOrThrow(
				'data-hover-media',
				container
			)
			if (mediaContainerPath === path) {
				container.classList.add('active')
				void window.Ucoast.mediaManager.loadAllInContainer(container)
			} else {
				container.classList.remove('active')
			}
		})
	}

	resetHoverMedia() {
		this.showMediaForPath('primary')
	}
}
